import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Typography,
  CircularProgress
} from '@mui/material'
import {
  People as PeopleIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material'
import { guardReportsApi, userService } from '../../services/api'
import { 
  UserWithProfile,
  GuardReportDoctorResponse
} from '../../types/api'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const formatCurrency = (value: string | number) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(numValue || 0)
}

const formatNumber = (value: string | number) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value
  return new Intl.NumberFormat('ro-RO', { maximumFractionDigits: 2 }).format(numValue || 0)
}

// Normalizare text pentru PDF (elimină diacritice)
const normalizePDFText = (text: string): string => {
  return text
    .replace(/ă/g, 'a')
    .replace(/Ă/g, 'A')
    .replace(/â/g, 'a')
    .replace(/Â/g, 'A')
    .replace(/î/g, 'i')
    .replace(/Î/g, 'I')
    .replace(/ș/g, 's')
    .replace(/Ș/g, 'S')
    .replace(/ț/g, 't')
    .replace(/Ț/g, 'T')
}

const GuardReportsDoctorTab: React.FC = () => {
  const today = new Date().toISOString().split('T')[0]
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]

  const [doctors, setDoctors] = useState<UserWithProfile[]>([])
  const [selectedDoctor, setSelectedDoctor] = useState('')
  const [startDate, setStartDate] = useState(firstDayOfMonth)
  const [endDate, setEndDate] = useState(today)
  
  const [report, setReport] = useState<GuardReportDoctorResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Export functions
  const exportToCSV = () => {
    if (!report) return
    
    const rows = [
      ['Raport Gardă - Medic'],
      [`Medic: ${report.doctor_name}`],
      [`Perioada: ${new Date(report.period_start).toLocaleDateString()} - ${new Date(report.period_end).toLocaleDateString()}`],
      [`Total Ore: ${formatNumber(report.total_hours)}h`],
      [`Total Generat: ${formatNumber(report.total_amount)} RON`],
      [],
      ['Instituție', 'Nr Ore', 'Preț Contract Medic (pe oră)', 'TOTAL']
    ]
    
    report.institutions.forEach(inst => {
      rows.push([
        inst.institution_name,
        formatNumber(inst.total_hours),
        formatNumber(inst.hourly_rate),
        formatNumber(inst.total_amount)
      ])
    })
    
    rows.push([])
    rows.push(['TOTAL GENERAL', formatNumber(report.total_hours), '', formatNumber(report.total_amount)])
    
    const csvContent = rows.map(row => row.join(',')).join('\n')
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `raport_garda_medic_${report.doctor_name}_${startDate}_${endDate}.csv`
    link.click()
  }

  const exportToPDF = () => {
    if (!report) return
    
    const doc = new jsPDF('portrait', 'mm', 'a4')
    
    // Title
    doc.setFontSize(16)
    doc.text(normalizePDFText('Raport Garda - Medic'), 14, 20)
    
    doc.setFontSize(10)
    doc.text(normalizePDFText(`Medic: ${report.doctor_name}`), 14, 28)
    doc.text(normalizePDFText(`Perioada: ${new Date(report.period_start).toLocaleDateString()} - ${new Date(report.period_end).toLocaleDateString()}`), 14, 34)
    
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.text(normalizePDFText(`Total Ore: ${formatNumber(report.total_hours)}h`), 14, 42)
    doc.text(normalizePDFText(`Total Generat: ${formatCurrency(report.total_amount)}`), 14, 49)
    doc.setFont('helvetica', 'normal')
    
    // Table with institutions
    const tableData = report.institutions.map(inst => [
      normalizePDFText(inst.institution_name),
      formatNumber(inst.total_hours) + 'h',
      formatNumber(inst.hourly_rate),
      formatNumber(inst.total_amount)
    ])
    
    // Add TOTAL row
    tableData.push([
      'TOTAL GENERAL',
      formatNumber(report.total_hours) + 'h',
      '',
      formatNumber(report.total_amount)
    ])
    
    autoTable(doc, {
      startY: 56,
      head: [[normalizePDFText('Institutie'), 'Nr Ore', normalizePDFText('Pret Contract Medic (RON/ora)'), 'TOTAL (RON)']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 30 },
        2: { cellWidth: 40 },
        3: { cellWidth: 35, fontStyle: 'bold' }
      },
      didParseCell: function(data) {
        // Make the last row (TOTAL) bold
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [240, 240, 240]
        }
      }
    })
    
    doc.save(normalizePDFText(`raport_garda_medic_${report.doctor_name}_${startDate}_${endDate}.pdf`))
  }

  useEffect(() => {
    const loadDoctors = async () => {
      try {
        const data = await userService.getUsers({ role: 'medic', limit: 200 })
        setDoctors(data.users)
      } catch (err) {
        console.error('Error loading doctors', err)
        setError('Eroare la încărcarea medicilor')
      }
    }
    loadDoctors()
  }, [])

  const handleGenerateReport = async () => {
    if (!selectedDoctor || !startDate || !endDate) {
      setError('Vă rugăm completați toate câmpurile')
      return
    }

    setError(null)
    setLoading(true)
    setReport(null)

    try {
      const request = {
        doctor_id: selectedDoctor,
        start_date: startDate,
        end_date: endDate
      }

      const reportData = await guardReportsApi.getDoctorReport(request)
      setReport(reportData)
    } catch (err: any) {
      console.error('Error generating report', err)
      setError(err.response?.data?.detail || 'Eroare la generarea raportului')
    } finally {
      setLoading(false)
    }
  }

  // Styling compact pentru input-uri
  const inputSx = { 
    '& .MuiInputBase-root': { fontSize: '0.75rem', height: 28 }, 
    '& .MuiInputLabel-root': { fontSize: '0.7rem' }
  }

  return (
    <Box>
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <PeopleIcon color="primary" sx={{ fontSize: '1rem' }} />
          Rapoarte Gardă - Medici
        </Typography>
        
        {/* Filters inline */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180, ...inputSx }}>
            <InputLabel>Medic</InputLabel>
            <Select
              label="Medic"
              value={selectedDoctor}
              onChange={(e) => setSelectedDoctor(e.target.value)}
            >
              <MenuItem value="" sx={{ fontSize: '0.75rem' }}>Selectează</MenuItem>
              {doctors.map((doc) => (
                <MenuItem key={doc.id} value={doc.id} sx={{ fontSize: '0.75rem' }}>
                  {doc.doctor_profile?.first_name} {doc.doctor_profile?.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            label="De la"
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 130, ...inputSx }}
          />
          
          <TextField
            label="Până la"
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 130, ...inputSx }}
          />
          
          <Button
            variant="outlined"
            size="small"
            onClick={handleGenerateReport}
            disabled={loading || !selectedDoctor}
            sx={{ height: 28, fontSize: '0.75rem', textTransform: 'none', px: 2 }}
          >
            {loading ? <CircularProgress size={14} /> : 'Generează'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {report && (
        <Paper sx={{ p: 1.5, '& .MuiTypography-root': { fontSize: '0.84rem', lineHeight: 1.15 }, '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
            {report.doctor_name} - Raport Gardă
          </Typography>
          <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">
            Perioada: {new Date(report.period_start).toLocaleDateString()} - {new Date(report.period_end).toLocaleDateString()}
          </Typography>
          
          <Box sx={{ my: 1.5, p: 1.5, bgcolor: 'primary.50', borderRadius: 1 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">Total Ore</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }} color="primary">{formatNumber(report.total_hours)}h</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography sx={{ fontSize: '0.75rem' }} color="text.secondary">Total Generat</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }} color="primary">{formatCurrency(report.total_amount)}</Typography>
              </Grid>
            </Grid>
          </Box>

          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, mb: 1 }}>
            Detalii per Instituție
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{
                  bgcolor: 'grey.900',
                  '& th': {
                    color: 'common.white',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    py: 1,
                    px: 1.5,
                    whiteSpace: 'nowrap'
                  }
                }}>
                  <TableCell>Instituție</TableCell>
                  <TableCell align="right">Ore</TableCell>
                  <TableCell align="right">Preț/Oră</TableCell>
                  <TableCell align="right">TOTAL</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.institutions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography sx={{ fontSize: '0.8rem' }} color="text.secondary">
                        Nicio gardă
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {report.institutions.map((inst) => (
                      <TableRow key={inst.institution_id}>
                        <TableCell>{inst.institution_name}</TableCell>
                        <TableCell align="right">{formatNumber(inst.total_hours)}h</TableCell>
                        <TableCell align="right">{formatCurrency(inst.hourly_rate)}</TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(inst.total_amount)}</strong>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontSize: '0.8rem' }}><strong>TOTAL</strong></TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontSize: '0.8rem' }}><strong>{formatNumber(report.total_hours)}h</strong></TableCell>
                      <TableCell align="right" sx={{ color: 'white' }}>—</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontSize: '0.8rem' }}>
                        <strong>{formatCurrency(report.total_amount)}</strong>
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<FileDownloadIcon sx={{ fontSize: '0.9rem' }} />} onClick={exportToPDF} sx={{ fontSize: '0.78rem' }}>
              PDF
            </Button>
            <Button variant="outlined" size="small" startIcon={<FileDownloadIcon sx={{ fontSize: '0.9rem' }} />} onClick={exportToCSV} sx={{ fontSize: '0.78rem' }}>
              CSV
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  )
}

export default GuardReportsDoctorTab

